import { useEffect } from "react";
import { useTranslation } from "react-i18next";

/** Languages written right-to-left. Arabic is the only one the app ships today. */
const RTL_LANGUAGES = ["ar"];

/** Default language of the app, used when i18n has not resolved one yet. */
const DEFAULT_LANGUAGE = "fr";

/** Base language tag: "ar-MA" -> "ar". */
const toBaseLanguage = (language: string | null | undefined): string =>
  (language || DEFAULT_LANGUAGE).split("-")[0];

/**
 * Reading direction of a language tag. The single source of truth for
 * direction in the app — use this instead of comparing against "ar" inline.
 */
export const getTextDirection = (
  language: string | null | undefined
): "rtl" | "ltr" =>
  RTL_LANGUAGES.includes(toBaseLanguage(language)) ? "rtl" : "ltr";

/**
 * Keeps `<html lang>` and `<html dir>` in sync with the active i18n language.
 *
 * The document element is what assistive technology and the browser's own
 * bidirectional text handling read, so a per-page `dir` is not enough. This is
 * mounted once, in `SessionProvider` — the component that resolves the language
 * — and is the only place that writes to `document.documentElement`.
 *
 * It re-runs on every language change (react-i18next re-renders on
 * `languageChanged`) and rewrites BOTH attributes each time, so switching away
 * from Arabic corrects the direction back to `ltr`.
 */
export const useDocumentLanguage = (): void => {
  const { i18n } = useTranslation();
  const language = toBaseLanguage(i18n.language);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("lang", language);
    root.setAttribute("dir", getTextDirection(language));
  }, [language]);
};
